import React, { ReactElement, useEffect, useState } from "react";

import WeibullPlot from "./plots/WeibullPlot";

import {
  linearRegression,
  weibullDistribution,
  WeibullProps,
  LinearRegressionProps,
  getXIntersections,
} from "../utils/utils";
import BoxPlots from "./plots/BoxPlots";

const getBox = (x: number[], name: string, color: string): any => {
  return {
    x: x,
    name: name,
    line: { color: color },
    type: "box",
    hoverinfo: "x",
  };
};

const getLine = (data: any, name: string, color: string): any => {
  return {
    ...data,
    name: name,
    line: { color: color },
  };
};

interface Props {
  failureData: any;
  yValue: string | null;
  failureCriterion: number;
  customFailures: any;
}

export default function Weibull({
  failureData,
  yValue,
  failureCriterion,
  customFailures,
}: Props): ReactElement {
  const [graphData, setGraphData] = useState<{
    wb: WeibullProps[];
    lg: LinearRegressionProps[];
    box: {
      x: number;
      line: { color: string };
      name: string;
      type: string;
      [key: string]: any;
    }[];
  }>({
    wb: [{ x: [], y: [], f: [] }],
    lg: [{ x: [], y: [] }],
    box: [],
  });

  const [intersections, setIntersections] = useState<string>("");

  useEffect(() => {
    let failure = failureData
      .map((e: any) => e.failureZyklus)
      .filter((e: any) => e);
    let failureWB = weibullDistribution(failure);
    let failureLG = linearRegression(failureWB.x, failureWB.y);

    let custom = Object.values(customFailures)
      .map((e: any) => e.zyklus)
      .filter((e) => e);
    let customWB = weibullDistribution(custom);
    let customLG = linearRegression(customWB.x, customWB.y);

    let error = failureData.map((e: any) => e.errorZyklus);
    let errorWB = weibullDistribution(error);
    let errorLG = linearRegression(errorWB.x, errorWB.y);

    let tmp = "";

    let failureIntersection = getXIntersections(failureLG);
    let customIntersection = getXIntersections(customLG);
    let errorIntersection = getXIntersections(errorLG);
    if (failureIntersection) {
      tmp += `${yValue}-${failureCriterion}: ${failureIntersection.toFixed(
        0
      )}<br>`;
    }
    if (customIntersection) {
      tmp += `Custom Failures: ${customIntersection.toFixed(0)}<br>`;
    }
    if (errorIntersection) {
      tmp += `Machine Log Failures: ${errorIntersection.toFixed(0)}<br>`;
    }

    setIntersections(tmp);

    setGraphData({
      wb: [
        getLine(failureWB, `${yValue}-${failureCriterion}`, "#0d6efd"),
        getLine(errorWB, "Machine Log Failures", "#fd7e14"),
        getLine(customWB, "Custom Failures", "#fd7ff4"),
      ],
      lg: [
        getLine(failureLG, "", "#0d6efd"),
        getLine(errorLG, "", "#fd7e14"),
        getLine(customLG, "", "#fd7ff4"),
      ],
      box: [
        getBox(failure, "  ", "#0d6efd"),
        getBox(error, " ", "#fd7e14"),
        getBox(custom, "", "#fd7ff4"),
      ],
    });
  }, [failureData, customFailures, yValue, failureCriterion]);

  return (
    <div>
      <WeibullPlot
        wb={graphData.wb}
        lg={graphData.lg}
        yValue={yValue}
        failureCriterion={failureCriterion}
        intersections={intersections}
      />
      <BoxPlots data={graphData.box} />
    </div>
  );
}
